import { Flex } from '@theme-ui/components';

import Loader from '@app/components/Loader';
import NotFound from '@app/components/NotFound';
import useAuthentication from '@app/hooks/useAuthentication';

import type { PricingPage_content_pricing_pages_layout_item_pricing_pages_multi_layout_products_products_id as DirectusProduct } from '../../__generated__/PricingPage';
import useShopProduct from '../../hook/useShopProduct';
import type { VariantProductExtendedType } from '../../SingleProduct/SingleProductLayout';
import BundleProductTile from './BundleProductTile';
import ConfigurableProductTile from './ConfigurableProductTile';
import VirtualProductTile from './VirtualProductTile';

const ProductTile = ({
  product,
  isPrimary,
}: {
  product: DirectusProduct;
  isPrimary: boolean;
}) => {
  const sku = product?.sku;

  const { loading, error, magentoProduct } = useShopProduct(sku);
  const { isAuthenticated, loading: isAuthenticatedLoading } =
    useAuthentication();

  if (loading || isAuthenticatedLoading) {
    return (
      <Flex sx={{ justifyContent: 'center' }}>
        <Loader />
      </Flex>
    );
  }

  if (error) {
    throw new Error('Unable to query product data');
  }

  if (!magentoProduct) {
    return <NotFound errorMessage="Product is not found in magento" />;
  }

  const isConfigurable =
    magentoProduct?.__typename === 'Magento2_ConfigurableProduct';

  const variants = isConfigurable ? magentoProduct?.variants : [];

  const niVariants =
    variants?.filter((variant) => {
      const productVariant = variant?.product as VariantProductExtendedType;

      return !productVariant?.categories?.find(
        (category) => category.name === 'NI Hidden',
      );
    }) ?? null;

  switch (magentoProduct.__typename) {
    case 'Magento2_ConfigurableProduct':
      return (
        <ConfigurableProductTile
          directusProduct={product}
          variants={niVariants}
          isAuthenticated={isAuthenticated}
          isPrimary={isPrimary}
        />
      );

    case 'Magento2_BundleProduct':
      return (
        <BundleProductTile
          directusProduct={product}
          magentoProduct={magentoProduct}
        />
      );

    case 'Magento2_VirtualProduct':
      return (
        <VirtualProductTile
          directusProduct={product}
          magentoProduct={magentoProduct}
        />
      );

    default:
      return (
        <NotFound
          errorMessage={`Unsupported product type: ${magentoProduct.__typename}`}
        />
      );
  }
};

export default ProductTile;
